import dayjs from 'dayjs';
import { TAppointmentDateTimeSlot, EAppointmentType, TAppointmentModalState } from './types';

export type DateOption = {
  date: Date;
  day: string;
  dateStr: string;
};

export type TimeOption = {
  time: string;
  start: string; // ISO string
  end: string; // ISO string
};

/**
 * Extract unique dates and their time slots from dateTimeSlots array
 */
export const extractDateAndTimeSlots = (
  dateTimeSlots: TAppointmentDateTimeSlot[],
  locale?: string,
  initialTimeSlot?: TAppointmentDateTimeSlot,
) => {
  let allSlots = [...dateTimeSlots];

  // Include initial time slot if it's not already in the list
  if (initialTimeSlot) {
    const isAlreadyIncluded = dateTimeSlots.some(
      (slot) => slot.start === initialTimeSlot.start
    );

    if (!isAlreadyIncluded) {
      allSlots.push(initialTimeSlot);
    }
  }

  // Group slots by date
  const slotsByDate = new Map<string, TAppointmentDateTimeSlot[]>();

  allSlots.forEach((slot) => {
    const date = dayjs(slot.start).format('YYYY-MM-DD');
    if (!slotsByDate.has(date)) {
      slotsByDate.set(date, []);
    }
    slotsByDate.get(date)!.push(slot);
  });

  // Create date options with locale-aware formatting
  const dateOptions: DateOption[] = Array.from(slotsByDate.keys())
    .sort() // Sort dates chronologically
    .map((dateStr) => {
      const date = dayjs(dateStr).toDate();

      // Use locale-aware formatting for day abbreviations
      const dayFormatter = new Intl.DateTimeFormat(locale || 'en-US', { weekday: 'short' });
      const day = dayFormatter.format(date);

      // Use locale-aware formatting for date (DD/MM for most locales, MM/DD for US)
      const dateFormatter = new Intl.DateTimeFormat(locale || 'en-US', {
        day: '2-digit',
        month: '2-digit',
      });
      const formattedDateStr = dateFormatter.format(date);

      return {
        date,
        day,
        dateStr: formattedDateStr,
      };
    });

  return {
    dateOptions,
    slotsByDate,
  };
};
// Helper function to generate bilingual appointment description
export const generateAppointmentDescription = (
  t: (key: string, options?: any) => string,
  clientName: string,
  clientEmail: string,
  clientPhone: string,
  appointmentType: EAppointmentType,
  duration: number,
  notes: string,
) => {
  const appointmentTypeText =
    appointmentType === EAppointmentType.GOOGLE_MEET
      ? t('scheduling.appointmentDescription.googleMeet')
      : t('scheduling.appointmentDescription.physicalMeeting');

  return `
    ${t('scheduling.appointmentDescription.contactInformation')}:
    ${t('scheduling.appointmentDescription.name')}: ${clientName}
    ${t('scheduling.appointmentDescription.email')}: ${clientEmail}
    ${t('scheduling.appointmentDescription.phone')}: ${clientPhone}

    ${t('scheduling.appointmentDescription.meetingType')}: ${appointmentTypeText}
    ${t('scheduling.appointmentDescription.duration')}: ${duration} ${t(
    'scheduling.appointmentDescription.minutes',
  )}
    ${notes ? `\n${t('scheduling.appointmentDescription.notes')}: ${notes}` : ''}
      `.trim();
};

export const generateAppointmentTitle = (
  t: (key: string, options?: any) => string,
  appointmentType: EAppointmentType,
  clientName: string,
) => {
  const appointmentTypeText =
    appointmentType === EAppointmentType.GOOGLE_MEET
      ? t('scheduling.appointmentDescription.googleMeet')
      : t('scheduling.appointmentDescription.physicalMeeting');

  return `${appointmentTypeText} - ${clientName}`;
};

export const defaultAppointmentModalState: TAppointmentModalState = {
  projectId: undefined,
  dateTimeSlot: undefined,
  appointmentType: EAppointmentType.GOOGLE_MEET,
  duration: 60,
  notes: '',
  name: '',
  email: '',
  phone: '',
};
